<script setup lang="ts">
import { computed, unref } from 'vue';

const props = defineProps({
  headerRef: {
    type: Object,
    required: true,
  },
});

// Access the internal state from the header component
const selectedCategories = computed(() => {
  if (!props.headerRef) return [];

  try {
    const ref = unref(props.headerRef);

    if (!ref) return [];

    const categories = ref.selectedCategories;

    if (!categories) return [];

    const value = unref(categories);

    return Array.isArray(value) ? value : [];
  } catch (e) {
    return [];
  }
});

const CATEGORY_PRIORITY_ORDER = [
  'novos',
  'roletas',
  'jogos ao vivo',
  'slots',
  'slots com cashback',
  'roletas com cashback',
];

const getCategoryPriority = (name: string): number => {
  const normalized = name.toLowerCase().trim();

  if (normalized === 'novos' || normalized === 'novidades') {
    return CATEGORY_PRIORITY_ORDER.indexOf('novos');
  }

  const index = CATEGORY_PRIORITY_ORDER.indexOf(normalized);

  return index === -1 ? CATEGORY_PRIORITY_ORDER.length : index;
};

const categoriesList = computed(() => {
  if (!props.headerRef) return [];

  try {
    const ref = unref(props.headerRef);

    if (!ref) return [];

    const list = ref.allCategoriesList;

    if (!list) return [];

    const value = unref(list);
    const categories = Array.isArray(value)
      ? value.filter((c: any) => {
          const slug = (c.slug ?? '').toLowerCase();
          const name = (c.name ?? '').toLowerCase().trim();

          return (
            slug !== 'todos' &&
            slug !== 'mais-pagos' &&
            slug !== 'all' &&
            name !== 'todos' &&
            name !== 'mais pagou' &&
            name !== 'mais pago'
          );
        })
      : [];

    categories.sort(
      (a: any, b: any) =>
        getCategoryPriority(a.name) - getCategoryPriority(b.name),
    );

    return categories;
  } catch (e) {
    console.error('Error accessing categoriesList:', e);

    return [];
  }
});

const setCategories = (newValue: string[]) => {
  if (props.headerRef && typeof props.headerRef === 'object') {
    const categoriesRef = props.headerRef.selectedCategories;

    if (
      categoriesRef &&
      typeof categoriesRef === 'object' &&
      'value' in categoriesRef
    ) {
      categoriesRef.value = newValue;
    } else if (Array.isArray(categoriesRef)) {
      categoriesRef.length = 0;
      categoriesRef.push(...newValue);
    }
  }
};

const toggleCategory = (slug: string) => {
  if (slug === 'mais-pagos') {
    if (selectedCategories.value.includes('mais-pagos')) {
      setCategories(['todos']);
    } else {
      setCategories(['mais-pagos']);
    }

    return;
  }

  const index = selectedCategories.value.indexOf(slug);

  if (index > -1) {
    setCategories(['todos']);
  } else {
    setCategories([slug]);
  }
};

const resetCategories = () => {
  setCategories(['todos']);
};

const stickyTabs = computed(() => {
  const fixed = [
    { name: 'Todos', value: 'todos' },
    { name: 'Mais Pagou', value: 'mais-pagos' },
  ];

  const dynamic = categoriesList.value.map((c: any) => ({
    name: c.name,
    value: c.slug,
  }));

  return [...fixed, ...dynamic];
});

const activeValue = computed(() => {
  if (selectedCategories.value.length === 0) return 'todos';

  return selectedCategories.value[0];
});

function onTabSelect(value: string | number) {
  const slug = String(value);

  if (slug === 'todos') {
    resetCategories();
  } else {
    toggleCategory(slug);
  }
}
</script>

<template>
  <BaseFilterTabs
    :model-value="activeValue"
    size="small"
    :tabs="stickyTabs"
    @update:model-value="onTabSelect"
  />
</template>
